import { computed, watch } from 'vue';
import { provideLocal, injectLocal } from '@vueuse/core';
import { storeToRefs } from 'pinia';

import useUpc from '~/modules/upc/composables/useUpc';
import { useProduct } from '~/modules/product/composables/useProduct';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import { usePageData } from '~/composables/usePageData';
import { PDP_WNM_REPOSITORY, type ProtectionPlanType } from '../constants';
import useSelectedPlan from './useSelectedPlan';
import { toUnderscore } from '~/utils/commonUtils';
import { useBrand } from '~/composables/useBrand';
import useExperiments from '~/stores/useExperimentsStore';

export function useWnmProvide() {
  const { isProtectionPlanEligibleFlag, isBigTicketItemFlag } = useProductFlags();
  const { killswitches } = useKillswitches();
  const {
    flags: { groupByProducts },
  } = usePageData();

  const anyMemberEligible = Object.values(groupByProducts || {}).some((member) => member?.productFlags?.isProtectionPlanEligibleFlag);

  if (!isProtectionPlanEligibleFlag && !anyMemberEligible) {
    provideLocal(PDP_WNM_REPOSITORY, {
      isWnmVisible: computed<boolean>(() => false),
      protectionPlans: computed(() => null),
      selectedPlan: computed(() => null),
      defaultPlanId: computed(() => -Infinity),
      isProductBigTicket: computed(() => false),
      setPlanIdEntryByUpc: () => {},
    });
    return;
  }

  const { isMcom } = useBrand();
  const { experimentFlags } = useExperiments();

  const { planType, selectedPlanId, updatePlanIdEntryByUpc, updateSelectedPlanIdByUpc } = useSelectedPlan();
  const { productData } = useProduct();
  const { selectedUpcId, upcData } = useUpc();

  const selectedUpc = computed(() => upcData.value?.upcs?.[selectedUpcId.value]);

  const isSelectedUpcAvailable = computed<boolean>(() => {
    if (!selectedUpcId.value) return false;
    const availability = selectedUpc.value?.availability;
    return !!(availability?.available || availability?.bopsAvailable || availability?.bopsAvailability);
  });

  // Get protection plans for the currently selected UPC
  const rawUpcs = productData?.relationships?.upcs;
  const protectionPlans = computed<ProtectionPlanType[]>(() => rawUpcs?.[selectedUpcId.value as number]?.protectionPlans ?? []);
  const defaultPlanId = computed(() => protectionPlans.value?.[0]?.id ?? -Infinity);

  const selectedPlan = computed<ProtectionPlanType | null>(() => protectionPlans.value?.find((x) => x.id === selectedPlanId.value) ?? null);

  const { memberFlags: memberProductFlags } = storeToRefs(useConsolidatedMemberStore());

  const isProtectionPlanEligible = computed<boolean>(() => isProtectionPlanEligibleFlag || !!memberProductFlags.value?.isProtectionPlanEligibleFlag);

  // TODOS - move this value to a reusable store later
  const isProductBigTicket = computed(() => isBigTicketItemFlag || !!memberProductFlags.value?.isBigTicketItemFlag);

  const isWnmVisible = computed<boolean>(
    () =>
      isProtectionPlanEligible.value &&
      (isMcom || experimentFlags.isWorryNoMoreEnabled) &&
      isSelectedUpcAvailable.value &&
      !!protectionPlans.value?.length &&
      (!isProductBigTicket.value || killswitches.isWNMBigTicketEnabled),
  );

  // Persist selected plan ID for the current UPC - updates on form submission
  function setPlanIdEntryByUpc() {
    if (!isSelectedUpcAvailable.value) return;
    updatePlanIdEntryByUpc(selectedUpcId.value as number);
  }

  // Restore previous selection when switching UPC
  watch(() => selectedUpcId.value, updateSelectedPlanIdByUpc);

  // Watch for plans to determine the API path (planType)
  const destroyWatcher = watch(
    () => protectionPlans.value,
    (plans) => {
      const _planType = toUnderscore(plans?.[0]?.displayType ?? '').toLowerCase();
      if (_planType) {
        planType.value = _planType;
        // Destroy watcher after initialization to prevent redundant overhead
        if (import.meta.client) setTimeout(() => destroyWatcher?.(), 0);
      }
    },
    { immediate: true },
  );

  provideLocal(PDP_WNM_REPOSITORY, {
    isWnmVisible,
    protectionPlans,
    selectedPlan,
    defaultPlanId,
    isProductBigTicket,
    setPlanIdEntryByUpc,
  });
}

/**
 * Injects the WNM context from a parent component.
 * If no context is found, it provides a new one locally.
 */
export function useWnm() {
  const wmn = injectLocal(PDP_WNM_REPOSITORY);

  if (!wmn) {
    throw new Error('useWnm() requires the provide composable, useProvideWnm(), to be called');
  }

  return wmn;
}
